import { TooltipProvider, styled } from '@moleculeone/ui';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { V4 } from '@moleculeone/utils';

import { LocationAnalogPrompt } from '@f/components/pages/dashboard/batch-searches/pages/create-batch-search/components/LocationAnalogPromptNode';

import { CompoundSpace } from './CompoundSpace';
import { CommonAtoms, CommonFragments, HotKeys, ModeToolbar, Toolbar } from './components';
import { DEFAULT_MODE, SELECT_MODE } from './etc/constants';
import { useCompoundEditor, useKeyboard } from './hooks';
import { molToSmiles, smilesToMol } from './utils/conversion';
import { setMode } from './utils/molecule-object/molecule-object.actions';

export const MENU = 'MENU';
export const ELEMENT = 'ELEMENT';
export const HOTKEYS = 'HOTKEYS';
export const FRAGMENT = 'FRAGMENT';
export const PICKER = 'PICKER';

export const EditorSpacer = styled('div', {
  variants: {
    isAnalogGenerationOpen: {
      true: {
        marginLeft: '1.2rem',
        marginRight: '1.2rem',
      },
      false: {
        marginLeft: '3.8rem',
        marginRight: '7rem',
      },
    },
  },
});

const Container = styled('div', {
  minWidth: 0,
});
const EditorContainer = styled('div', {
  display: 'flex',
  padding: '$5 0',
  flex: 1,
  variants: {
    isAnalogGenerationOpen: {
      true: {
        height: 'calc(60vh - 7rem)',
        maxHeight: 'calc(40rem - 7rem)',
        pointerEvents: 'none',
      },
      false: {
        height: '60vh',
        maxHeight: '40rem',
      },
    },
  },
});

const DrawingSpacesContainer = styled('div', {
  flexGrow: 1,
  display: 'flex',
  alignItems: 'center',
  position: 'relative',
  margin: '0 $3',
});

const TopContext = styled('div', {
  'position': 'relative',
  'zIndex': 2,
  'display': 'flex',
  'justifyContent': 'space-between',
  'alignItems': 'flex-end',
  'opacity': 0,
  'pointerEvents': 'none',
  'transform': 'translateY(-0.5rem)',
  'transition': 'opacity .2s, transform .2s',
  '&[data-visible=true]': {
    opacity: 1,
    transform: 'translateY(0)',
    pointerEvents: 'auto',
  },
});

const LeftContainer = styled('div', {
  'position': 'relative',
  'zIndex': 2,
  'opacity': 0,
  'pointerEvents': 'none',
  'transform': 'translateX(-0.5rem)',
  'transition': 'opacity .2s, transform .2s',
  '&[data-visible=true]': {
    opacity: 1,
    transform: 'translateY(0)',
    pointerEvents: 'auto',
  },
});

const RightContainer = styled('div', {
  'display': 'flex',
  'flexDirection': 'column',
  'justifyContent': 'space-between',
  'alignItems': 'flex-end',
  'opacity': 0,
  'pointerEvents': 'none',
  'transform': 'translateX(0.5rem)',
  'transition': 'opacity .2s, transform .2s',
  '&[data-visible=true]': {
    opacity: 1,
    transform: 'translateY(0)',
    pointerEvents: 'auto',
  },
  '&[hidden]': {
    opacity: 0,
    height: 0,
    pointerEvents: 'none',
  },
});
const RightToolbar = styled('div', {
  display: 'flex',
  gap: '$3',
  alignItems: 'flex-start',
});

export interface CompoundEditorProps {
  smiles: string;
  name: string;
  onSmilesChange: (smiles: string) => void;
  onNameChange: (name: string) => void;
  setOpen: (isOpen: boolean) => void;
  currentTargetIndex: number;
  isAnalogGenerationOpen: boolean;
  // addLocationPrompt: (locationAnalogPrompt: LocationAnalogPrompt) => void;
  // pruneLocationPrompts: () => void;
  // locationAnalogPromptNodes: React.ReactNode[];
  isSmilesError: boolean;
}
export const CompoundEditor = function ({
  smiles = '',
  name = '',
  onSmilesChange,
  onNameChange,
  setOpen,
  currentTargetIndex,
  isAnalogGenerationOpen,
  // addLocationPrompt,
  // pruneLocationPrompts,
  // locationAnalogPromptNodes,
  isSmilesError,
}: CompoundEditorProps) {
  const dispatch = useDispatch();

  const { spaces, lastMolsUpdate, actions, mode } = useCompoundEditor();
  const [areMolsInitialized, setMolsInitialized] = useState(false);

  const firstCompound = spaces[0];
  const compound = firstCompound;

  const [shouldShowError, setShouldShowError] = useState(false);
  const [previousTargetIndex, setPreviousTargetIndex] = useState<number | null>(null);

  useEffect(() => {
    if (previousTargetIndex !== currentTargetIndex) {
      setShouldShowError(isSmilesError);
      setPreviousTargetIndex(currentTargetIndex);
    }
  }, [currentTargetIndex, isSmilesError, previousTargetIndex]);

  // const compound = { ...firstCompound, locationAnalogPromptNodes };

  // useEffect(() => {
  //   const selectedAtoms = Object.values(firstCompound.drawnMol.atoms).filter(atom => atom.selected);
  //   if (selectedAtoms.length > 0) {
  //     const selectedAtomPositions = selectedAtoms.map(atom => atom.pos);
  //     const averageSelectedPosition = V4.average(...selectedAtomPositions);

  //     addLocationPrompt({
  //       location: averageSelectedPosition,
  //       prompt: '',
  //       atoms: selectedAtoms,
  //     });
  //   } else {
  //     pruneLocationPrompts();
  //   }
  // }, [firstCompound]);

  useEffect(() => {
    if (isAnalogGenerationOpen) {
      dispatch(setMode(SELECT_MODE));
    } else {
      dispatch(setMode(DEFAULT_MODE));
    }
  }, [isAnalogGenerationOpen]);

  useEffect(() => {
    const initalizeMol = async () => {
      const mol = await smilesToMol(smiles);
      actions.setMols([mol], true);
    };

    initalizeMol();
  }, [currentTargetIndex]);

  useEffect(() => {
    const updateSmiles = async () => {
      const smiles = await molToSmiles(compound.mol);
      onSmilesChange(smiles);
    };
    if (areMolsInitialized) {
      updateSmiles();
    }

    setMolsInitialized(true);
  }, [lastMolsUpdate]);

  useKeyboard({
    onKeyDown: event => {
      actions.onKeyDown(event);
    },
    onKeyUp: event => {
      if (event.key !== '?') {
        actions.onKeyUp(event);
      }
    },
  });

  return (
    <TooltipProvider>
      <Container data-editor>
        <EditorSpacer isAnalogGenerationOpen={isAnalogGenerationOpen}>
          <TopContext data-visible={!shouldShowError}>
            <Toolbar
              actions={actions}
              smiles={smiles}
              name={name}
              onNameChange={onNameChange}
              setOpen={setOpen}
              isAnalogGenerationOpen={isAnalogGenerationOpen}
            />
          </TopContext>
        </EditorSpacer>
        <EditorContainer isAnalogGenerationOpen={isAnalogGenerationOpen}>
          {!isAnalogGenerationOpen && (
            <LeftContainer data-visible={!shouldShowError}>
              <ModeToolbar />
            </LeftContainer>
          )}

          <DrawingSpacesContainer>
            <CompoundSpace compound={compound} actions={actions} isSmilesError={shouldShowError} />
          </DrawingSpacesContainer>

          {!isAnalogGenerationOpen && (
            <RightContainer data-visible={!shouldShowError}>
              <>
                <RightToolbar>
                  <CommonAtoms
                    mode={mode}
                    onPickAtom={mol => {
                      actions.onFragmentAtomMode(mol, mol.atoms[0].idx);
                    }}
                  />

                  <CommonFragments
                    mode={mode}
                    onPickAtom={(mol, idx) => {
                      actions.onFragmentAtomMode(mol, idx);
                    }}
                    onPickBond={(mol, idx) => {
                      actions.onFragmentBondMode(mol, idx);
                    }}
                  />
                </RightToolbar>

                <HotKeys />
              </>
            </RightContainer>
          )}
        </EditorContainer>
      </Container>
    </TooltipProvider>
  );
};
